import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';
import moment from 'moment';

//Components
import { Button } from '@gpstrackit/gtc-react-controls'
import Modal, { ModalBody, ModalFooter } from "Components/Modal";

//Helpers
import {
    getFormattedDateByTimeZone,
    GetFormatForMoment,
    getValueFloat,
    convertToKPH,
    convertToMI
} from "Core/data/Helpers";
import { endpoints } from 'Core/defaultValues';
import { useFeatureEnabled } from 'Components/FeatureToggle';
import { openChatList, openSupportChat, setIsExpanded } from 'Modules/chat/.globals/redux/actions';

//Steps
import Welcome from './.components/Welcome'
import Process from './.components/Process'

//Actions
import { catalogProcessItem } from 'Redux/actions';
import {
    troubleshootDeviceCreateUserTroubleshooting,
    troubleshootDeviceUpdateUserTroubleshooting,
    troubleshootDeviceGetOdometerEngineHours,
    troubleshootDeviceRebootVidFleetDevice,
    troubleshootDeviceCreateMessageOutbox,
    troubleshootDeviceCreateSfActivity,
    troubleshootDevicePingConfirmation,
    troubleshootDeviceGetLastReading,
    troubleshootDeviceGetSimDetails,
    troubleshootDeviceGetSimSession,
    troubleshootDeviceUpdateUnit,
    troubleshootDeviceGetUnit,
    editField,
    reset
} from './redux/slice';

//Styles
import './styles.scss';

const TroubleshootDevice = (props) => {
    const dispatch = useDispatch();
    const { messages, locale } = useIntl();

    //Redux States
    const {
        loadingsimCardSessionValidation,
        loadingInstallationValidation,
        loadingRebootVidfleetDevice,
        loadingRebootGeneralDevice,
        createUserTroubleshooting,
        loadingSimCardValidation,
        simCardSessionValidation,
        loadingSendPing,
        installationValidation,
        rebootVidfleetDevice,
        rebootGeneralDevice,
        locationValidation,
        loadingLastReading,
        confirmLastReading,
        newEngineOdometer,
        refreshLocation,
        pingSent,
        confirmLocation,
        pingConfirmation,
        loadingConfig,
        lastReadings,
        newOdometer,
        updateUnit,
        unitDetail,
        simDetails,
        simSession,
        sequence,
        config,
        simCardValidation,
        open,
        unit
    } = useSelector(state => state.troubleshootDeviceRedux);
    const user = useSelector((state) => state.securityRedux.user);
    const authId = useSelector((state) => state.securityRedux.authId);
    const isChatListOpen = useSelector((state) => state.chatsContainerRedux.openModal);
    const enableNewSupportChat = useFeatureEnabled('support-chat');

    //Local States
    const [troubleshootingResults, setTroubleshootingResults] = useState({ steps: [] });
    const [formData, setFormData] = useState({});
    const [expanded, setExpanded] = useState(1);
    const [step, setStep] = useState(1);
    const [pingValidation, setPingValidation] = useState(0);

    useEffect(() => {
        if (step === 2 && createUserTroubleshooting?.id && open) {
            if (expanded === 1) {
                if (!unitDetail) dispatch(troubleshootDeviceGetUnit({ id: unit?.id }))
            } else if (expanded === 2) {
                if (!lastReadings) {
                    dispatch(troubleshootDeviceGetLastReading({ id: unit?.id }))
                }
            } else if (expanded === 3 && (unitDetail?.deviceModel?.odometerEnable || unitDetail?.deviceModel?.hoursEnable)) {
                if (!config) dispatch(troubleshootDeviceGetOdometerEngineHours({ id: unit?.id }))
            }
        }
    }, [expanded, step, createUserTroubleshooting])

    useEffect(() => {
        if (unitDetail?.id) {
            let currentTroubleshootingResults = { ...troubleshootingResults }
            let currentSequence = sequence
            //Validation if the device is installed
            if (unitDetail?.installed) {

                dispatch(editField({ field: "installationValidation", value: { error: false, data: { installed: true, installedOn: unitDetail?.installedOn } } }))
                currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: true, step: "Connection History - Installation History", results: { message: "Unit installed", data: { installed: true, installedOn: unitDetail?.installedOn } } })
                dispatch(editField({ field: "sequence", value: currentSequence }))

                //Validate if the device is AT&T
                if (unitDetail?.simCard?.simType && unitDetail?.simCard?.simType?.toLowerCase().includes("att")) {
                    dispatch(troubleshootDeviceGetSimDetails({ iccId: unitDetail?.simCard?.iccId }))
                } else {
                    currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: true, step: "Connection History - Cellular History", results: { message: "Not applicable" } })
                    dispatch(editField({ field: "sequence", value: currentSequence }))
                    dispatch(reset({
                        loadingInstallationValidation: false,
                        loadingSimCardValidation: false,
                        loadingsimCardSessionValidation: false,
                        simCardSessionValidation: { error: false, data: { status: "Not applicable" } },
                        simCardValidation: { error: false, data: { status: "Not applicable" } },
                        attValidation: false
                    }))
                }
            } else {
                //If the device is not installed, the process ends
                currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: false, step: "Connection History - Installation History", results: { message: "Unit not installed", data: { installed: false } } })
                dispatch(editField({ field: "sequence", value: currentSequence }))
                dispatch(reset({
                    installationValidation: { error: true, data: { installed: false, installedOn: null } },
                    loadingsimCardSessionValidation: false,
                    loadingSimCardValidation: false,
                }))
            }
            updateFlow(currentTroubleshootingResults)
        }
    }, [unitDetail])

    useEffect(() => {
        if (simDetails) {
            try {
                let currentSequence = sequence
                //Validation of sim card status
                let currentTroubleshootingResults = { ...troubleshootingResults }
                let data = JSON.parse(simDetails)
                if (data && data?.status === "ACTIVATED") {
                    dispatch(editField({ field: "simCardValidation", value: { error: false, data: { ...data } } }))
                    dispatch(troubleshootDeviceGetSimSession({ iccId: unitDetail?.simCard?.iccId }))
                } else {
                    //If the sim card is inactive, the process is terminated.
                    currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: false, step: "Connection History - Cellular History", results: { message: "The sim card is deactivated", data: { status: data?.status } } })
                    dispatch(editField({ field: "sequence", value: currentSequence }))
                    dispatch(editField({ field: "simCardValidation", value: { error: true, data: { ...data } } }))
                    dispatch(troubleshootDeviceGetSimSession({ iccId: unitDetail?.simCard?.iccId }))
                }
                updateFlow(currentTroubleshootingResults)
            } catch (error) { }
        }
    }, [simDetails])

    useEffect(() => {
        if (simSession) {
            try {
                //Sim card session validation
                let currentTroubleshootingResults = { ...troubleshootingResults }
                let data = JSON.parse(simSession)
                let currentSimDetails = JSON.parse(simDetails)
                let currentSequence = sequence
                /* if (data?.dateSessionEnded) {
                    const dateSessionEnded = moment(data?.dateSessionEnded);
                    const currentDate = moment();
                    const totalMinutes = currentDate.diff(dateSessionEnded, 'minutes');

                    //For Asset the time limit is 4320 minutes and for Fleet it is 1440 minutes
                    if (unitDetail.deviceCategory === "Asset" && totalMinutes > 4320 || unitDetail.deviceCategory === "Fleet" && totalMinutes > 1440) {
                        dispatch(editField({ field: "simCardSessionValidation", value: { error: true, data: { status: currentSimDetails?.status, exceededLimit: true, ...data } } }))
                        currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: false, step: "Connection History - Cellular History", results: { message: "We've detected that the device SIM has connectivity issues", data: { dateSessionEnded: data?.dateSessionEnded, status: currentSimDetails?.status } } })
                    } else {
                        dispatch(editField({ field: "simCardSessionValidation", value: { error: false, data: { status: currentSimDetails?.status, exceededLimit: false, ...data } } }))
                        currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: true, step: "Connection History - Cellular History", results: { message: "OK", data: { status: currentSimDetails?.status, dateSessionEnded: data?.dateSessionEnded } } })
                    }
                } else if (data?.dateSessionStarted) { */
                currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: true, step: "Connection History - Cellular History", results: { message: "OK", data: { /* dateSessionStarted: data?.dateSessionStarted, */ status: currentSimDetails?.status } } })
                dispatch(editField({ field: "simCardSessionValidation", value: { error: false, data: { status: currentSimDetails?.status/* , ...data */ } } }))
                /* } */
                dispatch(editField({ field: "sequence", value: currentSequence }))
                updateFlow(currentTroubleshootingResults)
            } catch (error) { }
        }
    }, [simSession])

    useEffect(() => {
        if (lastReadings && !refreshLocation && !pingConfirmation) {
            let currentTroubleshootingResults = { ...troubleshootingResults }
            let currentSequence = sequence
            if (!lastReadings.error) {
                currentTroubleshootingResults.steps.push({
                    sequence: currentSequence += 1,
                    passed: true,
                    step: "Location", results: { message: `Latitude: ${lastReadings?.latitude}, Longitude: ${lastReadings?.longitude}, hDop: ${lastReadings?.hDop}`, data: lastReadings }
                })
            } else {
                currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: false, step: "Location", results: { message: "No last reading for this unit" } })
            }
            dispatch(editField({ field: "sequence", value: currentSequence }))
            updateFlow(currentTroubleshootingResults)
        }
    }, [lastReadings])

    useEffect(() => {
        if (config) {
            const momentFormat = user?.dateformat ? `${GetFormatForMoment(user.dateformat)} h:mm a` : null;
            let currentConfig = {
                ...config,
                useEcuOdometer: config.useEcuOdometer || false,
                engineOdometer: config.lastReading?.engineOdometer && Number(config.lastReading?.engineOdometer).toFixed(2) || 0,
                ecuEngineOdometer: config.lastReading?.ecuEngineOdometer && Number(config.lastReading?.ecuEngineOdometer).toFixed(2) || 0,
                offsetEcuEngineOdometer: config.offsetEcuEngineOdometer && Number(config.offsetEcuEngineOdometer).toFixed(2) || 0,
                offsetEngineOdometer: config.offsetEngineOdometer && Number(config.offsetEngineOdometer).toFixed(2) || 0,
                useEcuEngineHours: config.useEcuEngineHours || false,
                engineHours: config.lastReading?.engineHours && Number(config.lastReading?.engineHours).toFixed(2) || 0,
                ecuEngineHours: config.lastReading?.ecuEngineHours && Number(config.lastReading?.ecuEngineHours).toFixed(2) || 0,
                offsetEcuEngineHours: config.offsetEcuEngineHours && Number(config?.offsetEcuEngineHours).toFixed(2) || 0,
                offsetEngineHours: config.offsetEngineHours && Number(config.offsetEngineHours).toFixed(2) || 0,
                offsetEngineHoursUpdatedOnLabel: getFormattedDateByTimeZone(config.offsetEngineHoursUpdatedOn, user?.timeZone, messages, momentFormat, false, locale),
                offsetEngineOdometerUpdatedOnLabel: getFormattedDateByTimeZone(config.offsetEngineOdometerUpdatedOn, user?.timeZone, messages, momentFormat, false, locale),
            }

            if (currentConfig.useEcuOdometer) {
                currentConfig.dashOdometer = Number(getValueFloat(currentConfig.offsetEcuEngineOdometer) + getValueFloat(currentConfig.ecuEngineOdometer)).toFixed(2) || 0;
            } else {
                currentConfig.dashOdometer = Number(getValueFloat(currentConfig.offsetEngineOdometer) + getValueFloat(currentConfig.engineOdometer)).toFixed(2) || 0;
            }

            currentConfig.dashOdometer = currentConfig.dashOdometer > 100000000 ? 100000000 : currentConfig.dashOdometer;

            if (user.isKph) {
                currentConfig.dashOdometer = convertToKPH(currentConfig.dashOdometer)
                currentConfig.ecuEngineOdometer = convertToKPH(currentConfig.ecuEngineOdometer)
                currentConfig.offsetEcuEngineOdometer = convertToKPH(currentConfig.offsetEcuEngineOdometer)
                currentConfig.engineOdometer = convertToKPH(currentConfig.engineOdometer)
                currentConfig.offsetEngineOdometer = convertToKPH(currentConfig.offsetEngineOdometer)
            }

            currentConfig.totalOdometer = currentConfig.dashOdometer

            if (currentConfig.useEcuEngineHours) {
                currentConfig.dashEngineHours = Number(getValueFloat(currentConfig.offsetEcuEngineHours) + getValueFloat(currentConfig.ecuEngineHours)).toFixed(2) || 0;
            } else {
                currentConfig.dashEngineHours = Number(getValueFloat(currentConfig.offsetEngineHours) + getValueFloat(currentConfig.engineHours)).toFixed(2) || 0;
            }

            currentConfig.dashEngineHours = currentConfig.dashEngineHours > 1000000 ? 1000000 : currentConfig.dashEngineHours;
            currentConfig.totalEngineHours = currentConfig.dashEngineHours;

            if (isNaN(Number(currentConfig.dashEngineHours))) {
                currentConfig.dashEngineHours = null
                currentConfig.totalEngineHours = '0';
            }
            currentConfig.totalEngineHours = currentConfig.dashEngineHours;
            let currentSequence = sequence
            let currentTroubleshootingResults = { ...troubleshootingResults }

            currentTroubleshootingResults.steps.push({ sequence: currentSequence += 1, passed: true, step: "Odometer and Engine Hours", results: { message: `Current Odometer: ${currentConfig.totalOdometer || 0}, Current Engine Hours: ${currentConfig.totalEngineHours || 0}` } })
            dispatch(editField({ field: "sequence", value: currentSequence }))
            updateFlow(currentTroubleshootingResults)

            setFormData({ ...currentConfig })
        }
    }, [config])

    useEffect(() => {
        if (pingSent) {
            if (!pingSent?.result) {
                dispatch(reset({ loadingSendPing: false }))
            } else {
                setTimeout(() => {
                    setPingValidation(prep => prep + 1)
                    dispatch(troubleshootDevicePingConfirmation({ id: unit?.id }))
                }, 15000)
            }
        }
    }, [pingSent])

    useEffect(() => {
        if (pingConfirmation) {
            if (pingConfirmation) {
                let currentTroubleshootingResults = { ...troubleshootingResults }
                let currentSequence = sequence
                if (lastReadings?.LastMessageTime === pingConfirmation?.LastMessageTime) {
                    if (pingValidation < 8) {
                        dispatch(reset({ pingConfirmation: null }))
                        setTimeout(() => {
                            setPingValidation(prep => prep + 1)
                            dispatch(troubleshootDevicePingConfirmation({ id: unit?.id }))
                        }, 15000)
                    } else {
                        currentTroubleshootingResults.steps.push({
                            sequence: currentSequence += 1,
                            passed: false,
                            step: "Location", results: { message: 'Unit did not respond' }
                        })
                        dispatch(reset({ loadingSendPing: false, confirmLocation: { error: true } }))
                    }
                }
                if (pingConfirmation?.LastMessageTime > lastReadings.LastMessageTime) {
                    dispatch(reset({
                        loadingSendPing: false,
                        refreshLocation: true,
                        lastReadings: pingConfirmation
                    }))
                    currentTroubleshootingResults.steps.push({
                        sequence: currentSequence += 1,
                        passed: true,
                        step: "Location", results: { message: `New Latitude: ${pingConfirmation?.latitude}, New Longitude: ${pingConfirmation?.longitude}, New hDop: ${pingConfirmation?.hDop}`, data: pingConfirmation }
                    })
                }
                dispatch(editField({ field: "sequence", value: currentSequence }))
                updateFlow(currentTroubleshootingResults)
            }
        }
    }, [pingConfirmation])

    useEffect(() => {
        if (updateUnit) {
            let payloadUnit = {
                id: formData.id,
                CUnits_id: formData.id,
                offsetEcuEngineOdometer: formData.offsetEcuEngineOdometer,
                offsetEngineOdometer: formData.offsetEngineOdometer,
                useEcuEngineHours: formData.useEcuEngineHours,
                offsetEcuEngineHours: formData.offsetEcuEngineHours,
                offsetEngineHours: formData.offsetEngineHours,
            }
            dispatch(catalogProcessItem("Unit", { ...payloadUnit }));
            let payloadLastReading = {
                deviceId: formData.id,
                engineOdometer: formData.engineOdometer,
                ecuEngineOdometer: formData.ecuEngineOdometer,
                engineHours: formData.engineHours
            }
            dispatch(catalogProcessItem("LastReading", { ...payloadLastReading }))
        }
    }, [updateUnit])

    useEffect(() => {
        setTimeout(() => {
            if (window.embedded_svc?.liveAgentAPI) {
                window.embedded_svc.addEventHandler(
                    "onAvailability",
                    function (data) {
                        if (data.isAgentAvailable) {
                            dispatch(editField({ field: "isAgentAvailable", value: true }))
                        } else {
                            dispatch(editField({ field: "isAgentAvailable", value: false }))
                        }
                    }
                );
                window.embedded_svc.liveAgentAPI.ping();
            }
        }, 2000);
    }, [])



    const handleClose = () => {
        if (!loadingInstallationValidation && !loadingSimCardValidation && !loadingLastReading && !loadingConfig || step === 1) {
            setTroubleshootingResults({ steps: [] });
            /* setSimCardValidation({}); */
            dispatch(reset());
            setExpanded(1);
            setStep(1);
            let currentTroubleshootingResults = { ...troubleshootingResults };
            if (step === 2 && createUserTroubleshooting?.id && expanded !== 4) {
                let validateStep = currentTroubleshootingResults.steps.findIndex(item => item.step === "Summary")
                if (validateStep > -1) {
                    currentTroubleshootingResults.steps.splice(validateStep, 1);
                }
                const stepNames = [messages['troubleshootDevice_connectionHistory'], messages['troubleshootDevice_location'], messages['troubleshootDevice_OdometerEngineHours'], messages['troubleshootDevice_summary']];
                const stepName = stepNames[expanded - 1] || "Unknown";
                const newStep = {
                    sequence: sequence + 1,
                    passed: true,
                    step: stepName,
                    results: { message: "The user troubleshooting process is complete" }
                };
                currentTroubleshootingResults.steps.push(newStep);
                updateFlow(currentTroubleshootingResults);
            }
        }
    };

    const sendCommand = (action) => {
        let command = {
            via: "SMS",
            message: {
                cannedCommand: {
                    commandKey: action,
                    action: 'default'
                }
            },
            description: `SMS Notification`,
            to: [{ entityName: "Unit", entityId: unit?.id }]
        };
        if (unitDetail?.type === "queclink") {
            if (unitDetail?.deviceModel?.unitTypeId === 31) {
                command.message.cannedCommand.action = `gl500mg`
            } else if (unitDetail?.deviceModel?.unitTypeId === 32) {
                command.message.cannedCommand.action = `gv50mg`
            }
        }
        let currentTroubleshootingResults = { ...troubleshootingResults }
        let currentSequence = sequence + 1
        if (action === "ping") {
            dispatch(troubleshootDeviceCreateMessageOutbox({ command, refreshLocation: true }))
        } else if (action === "reset") {
            dispatch(troubleshootDeviceCreateMessageOutbox({ command, resetUnit: true }))
        }
        currentTroubleshootingResults.steps.push({ sequence: currentSequence, passed: true, step: "Location", results: { message: `Command ${action} sent to the unit` } })
        dispatch(editField({ field: "sequence", value: currentSequence }))
        updateFlow(currentTroubleshootingResults)
    }

    const resetDevice = () => {
        if (unitDetail?.type?.toUpperCase() === "VIDFLEET") {
            dispatch(troubleshootDeviceRebootVidFleetDevice(unit?.id))
            let currentTroubleshootingResults = { ...troubleshootingResults }
            let currentSequence = sequence + 1
            currentTroubleshootingResults.steps.push({ sequence: currentSequence, passed: true, step: "Location", results: { message: `Reset command sent to vidfleet unit` } })
            dispatch(editField({ field: "sequence", value: currentSequence }))
            updateFlow(currentTroubleshootingResults)
        } else sendCommand("reset")
    }

    const updateFlow = (data) => {
        let payload = {
            id: data.id,
            flow: JSON.stringify({ steps: data.steps })
        }
        setTroubleshootingResults(data)
        dispatch(troubleshootDeviceUpdateUserTroubleshooting(payload))
    }

    const startProcess = () => {
        let troubleshootingResults = {
            id: uuidv4(),
            unitId: unit?.id,
            flow: JSON.stringify({ steps: [] }),
            steps: []
        }
        dispatch(troubleshootDeviceCreateUserTroubleshooting(troubleshootingResults))
        delete troubleshootingResults['flow']
        setTroubleshootingResults(troubleshootingResults);
        setStep(2);
    }

    const updateOdometerEngineHours = () => {
        let payload = {
            id: formData?.id,
            offsetEcuEngineHours: formData?.offsetEcuEngineHours,
            offsetEcuEngineHoursUpdatedOn: formData?.offsetEcuEngineHoursUpdatedOn,
            offsetEcuEngineOdometer: formData?.offsetEcuEngineOdometer,
            offsetEcuEngineOdometerUpdatedOn: formData?.offsetEcuEngineOdometerUpdatedOn,
            offsetEngineHours: formData?.offsetEngineHours,
            offsetEngineHoursUpdatedOn: formData?.offsetEngineHoursUpdatedOn,
            offsetEngineOdometer: formData?.offsetEngineOdometer,
            offsetEngineOdometerUpdatedOn: formData?.offsetEngineOdometerUpdatedOn,
            useEcuEngineHours: formData?.useEcuEngineHours,
            useEcuOdometer: formData?.useEcuEngineHours,
            useEcuSpeed: formData?.useEcuEngineHours,
            useEcuVin: formData?.useEcuVin,
        }

        if (user.isKph) {
            payload.offsetEngineOdometer = convertToMI(payload.offsetEngineOdometer)
            payload.offsetEcuEngineOdometer = convertToMI(payload.offsetEcuEngineOdometer)
        }

        dispatch(troubleshootDeviceUpdateUnit(payload))

        let currentTroubleshootingResults = { ...troubleshootingResults }
        let validateStep = currentTroubleshootingResults.steps.findIndex(item => item.step === "Odometer and Engine Hours")
        if (validateStep > -1) {
            currentTroubleshootingResults.steps[validateStep].results = { message: `New Odometer: ${formData.dashOdometer || 0}, New Engine Hours: ${formData.dashEngineHours || 0}` }
        }
        updateFlow(currentTroubleshootingResults)
    }

    const chatHandler = () => {
        let subject = `User Troubleshooting`
        let description = ""

        if (createUserTroubleshooting?.id) {
            description = `
                Unit: ${unit?.label}
                Serial: ${unitDetail?.esn}
                Url: ${endpoints.PM_URL}/app/devices/overview/${unit?.id}\n
            `
            if (troubleshootingResults?.steps?.length) {
                troubleshootingResults?.steps.map((item) => {
                    description += `Step: ${item.step}\nMessage: ${item?.results?.message}\nPassed: ${item?.passed ? "Yes" : "No"}\n\n`
                })
            }
        }
        dispatch(troubleshootDeviceCreateSfActivity({ subject: subject, description: description }))

        if (enableNewSupportChat) {
            dispatch(setIsExpanded(true));
            if (!isChatListOpen) {
                dispatch(openChatList({
                    variableQuery: { userId: JSON.stringify(authId) }
                }));
            }
            dispatch(openSupportChat({ chatId: 'support' }));
            return;
        }

        dispatch(reset({ openChat: true }))
        setTimeout(() => dispatch(editField({ field: "openChat", value: false })), 1000)
    }

    return (
        <Modal
            title={`${messages['troubleshootDevice_title']}: ${unit?.label || ''}`}
            open={open}
            position="right"
            size="small"
            handleClose={handleClose}
            headerIcon="troubleshoot"
        >
            <ModalBody>
                <div className="troubleshoot-device-container">
                    {step === 1 ? (
                        <Welcome
                            startProcess={startProcess}
                            openChat={chatHandler}
                        />
                    ) : (
                        <Process
                            expanded={expanded}
                            setExpanded={setExpanded}
                            formData={formData}
                            setFormData={setFormData}
                            resetDevice={resetDevice}
                            openChat={chatHandler}
                        />
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <div className="troubleshoot-device-footer">
                    {
                        step === 2 && (
                            <>
                                <Button
                                    isDisabled={
                                        loadingsimCardSessionValidation ||
                                        loadingInstallationValidation ||
                                        loadingSimCardValidation ||
                                        loadingLastReading ||
                                        loadingSendPing ||
                                        loadingRebootVidfleetDevice ||
                                        installationValidation?.error ||
                                        simCardSessionValidation?.error ||
                                        loadingRebootGeneralDevice ||
                                        lastReadings?.error ||
                                        loadingConfig ||
                                        (unitDetail?.type?.toUpperCase() === "VIDFLEET" && locationValidation?.error) ||
                                        confirmLastReading === "2"
                                    }
                                    variant="secondary"
                                    onClick={(e) => {
                                        if (e) e.preventDefault();
                                        if ((refreshLocation && locationValidation?.error) || rebootGeneralDevice || rebootVidfleetDevice) handleClose()
                                        else {
                                            if (expanded === 1) {
                                                let currentUnit = unit
                                                setStep(prevStep => prevStep - 1)
                                                dispatch(reset())
                                                dispatch(reset({ open: true, unit: currentUnit }))
                                            } else setExpanded(prevStep => prevStep - 1)
                                        }
                                    }}
                                >
                                    {expanded > 1 ? messages['back'] : messages['cancel']}
                                </Button>
                                <Button
                                    style={{ marginLeft: 5 }}
                                    isDisabled={
                                        loadingsimCardSessionValidation ||
                                        loadingInstallationValidation ||
                                        loadingSimCardValidation ||
                                        loadingLastReading ||
                                        loadingSendPing ||
                                        loadingRebootVidfleetDevice ||
                                        installationValidation?.error ||
                                        simCardSessionValidation?.error ||
                                        simCardValidation?.error ||
                                        loadingRebootGeneralDevice ||
                                        lastReadings?.error ||
                                        rebootGeneralDevice ||
                                        rebootVidfleetDevice ||
                                        loadingConfig ||
                                        (unitDetail?.type?.toUpperCase() === "VIDFLEET" && locationValidation?.error) ||
                                        confirmLocation?.error ||
                                        confirmLastReading === "2"
                                    }
                                    onClick={(e) => {
                                        if (e) e.preventDefault();
                                        if (expanded === 4) handleClose();
                                        else if (locationValidation?.error && !refreshLocation) {
                                            sendCommand("ping")
                                        } else {
                                            if (step < 2) setStep(prevStep => prevStep + 1)
                                            else {
                                                let currentExpanded = expanded + 1
                                                if ((currentExpanded === 4 && (unitDetail?.deviceModel?.odometerEnable || unitDetail?.deviceModel?.hoursEnable)) || (currentExpanded === 3 && (!unitDetail?.deviceModel?.odometerEnable && !unitDetail?.deviceModel?.hoursEnable))) {
                                                    let currentTroubleshootingResults = { ...troubleshootingResults }
                                                    let validateStep = currentTroubleshootingResults.steps.findIndex(item => item.step === "Summary")
                                                    if (validateStep > -1) {
                                                        currentTroubleshootingResults.steps.splice(validateStep, 1)
                                                    }
                                                    currentTroubleshootingResults.steps.push({ sequence: sequence + 1, passed: true, step: "Summary", results: { message: 'The user troubleshooting process is complete' } })
                                                    dispatch(editField({ field: "sequence", value: sequence + 1 }))
                                                    updateFlow(currentTroubleshootingResults)
                                                }
                                                setExpanded(currentExpanded)
                                                if (!updateUnit && (newOdometer === "2" || newEngineOdometer === "2")) updateOdometerEngineHours();
                                            }
                                        }
                                    }}
                                    variant='primary'
                                >
                                    {step === 1
                                        ? messages['start']
                                        : expanded === 4 ? "End" : locationValidation?.error
                                            ? refreshLocation || unitDetail?.type?.toUpperCase() === "VIDFLEET" || unitDetail?.simCard?.simType === "USC"
                                                ? messages['next'] : "PING" : messages['next']}
                                </Button>
                            </>
                        )
                    }
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default TroubleshootDevice;